import { useAuth0 } from '@auth0/auth0-react'
import { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useHistory } from 'react-router'

import OrnaLogo from 'src/assets/ORNA Square Logo, transparent bg.png'
import CommonBanner from 'src/components/common/banner'
import openNotification from 'src/components/common/notification'
import Spinner from 'src/components/common/spinner'
import withSentryWrapper from 'src/components/common/withSentryWrapper'
import alerts from 'src/constants/alerts'
import OrganizationContext from 'src/context/organizationContext'
import RedirectSignupContext from 'src/context/redirectSignupContext'
import UserEmailContext from 'src/context/userEmailContext'
import { APP_ENV } from 'src/env'
import routes from 'src/routes'
import { handlePromise } from 'src/utils/handlePromise'
import useLogout from 'src/utils/hooks/useLogout'
import { useOrnaBackendClient } from 'src/utils/hooks/useOrnaBackendClient'
import useQueryStrings from 'src/utils/hooks/useQueryStrings'
import logger from 'src/utils/logger'

const SelectOrganizationView = ({ setNavigateTo }) => {
    // const SelectOrganizationView = () => {
    const { isLoading } = useAuth0()
    const logout = useLogout()
    const { redirectState } = useContext(RedirectSignupContext)
    const { setOrganizationId } = useContext(OrganizationContext)
    const { userEmail } = useContext(UserEmailContext)

    const history = useHistory()
    const ornaBackendClient = useOrnaBackendClient()
    const isSmallScreen = useMediaQuery({ maxWidth: '700px' })

    const [organizationsLoading, setOrganizationsLoading] = useState(true)
    const [organizations, setOrganizations] = useState(null)

    const qStrings = useQueryStrings()

    const redirectLink = useMemo(() => {
        return qStrings.get('from') ?? `/`
    }, [qStrings])

    const [errorFetchingOrganizations, setErrorFetchingOrganizations] =
        useState(false)

    // action to run when organization is chosen
    const handleSelectOrganization = (organizationId) => {
        localStorage.setItem(
            'userOrganizations',
            organizations ? organizations.length : 0
        )
        setOrganizationId(organizationId)
    }

    // run data loader on mount
    useEffect(() => {
        const authorizeUser = async () => {
            const [err, response] = await handlePromise(
                ornaBackendClient.get(
                    APP_ENV.REACT_APP_ORNA_BACKEND_URL + '/authorize-user',
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                )
            )

            setOrganizationsLoading(false)

            if (err) {
                logger.error(err)
                setErrorFetchingOrganizations(true)
            } else {
                if (response && response.data.organizations?.length > 0) {
                    setErrorFetchingOrganizations(false)
                    setOrganizations(response.data.organizations)
                    localStorage.setItem(
                        'userOrganizations',
                        response.data.organizations.length
                    )

                    if (
                        qStrings.has('organizationId') &&
                        typeof qStrings.get('organizationId') === 'string'
                    ) {
                        const found = response.data.organizations.find(
                            (org) =>
                                org.organizationId ===
                                qStrings.get('organizationId')
                        )
                        if (found) {
                            localStorage.setItem(
                                'userOrganizationId',
                                found.organizationId
                            )
                            setOrganizationId(found.organizationId)
                            setNavigateTo(redirectLink)
                        }
                    }

                    // if (response.data.organizations.length === 1) {
                    //     localStorage.setItem(
                    //         'userOrganizationId',
                    //         response.data.organizations[0].organizationId
                    //     )
                    //     setOrganizationId(
                    //         response.data.organizations[0].organizationId
                    //     )
                    //     setNavigateTo(redirectLink)
                    //     return
                    // }

                    //auto select the organization ONLY when redirectState is nullish
                    if (
                        response.data.organizations.length === 1 &&
                        !redirectState
                    ) {
                        localStorage.setItem(
                            'userOrganizationId',
                            response.data.organizations[0].organizationId
                        )
                        setOrganizationId(
                            response.data.organizations[0].organizationId
                        )
                        setNavigateTo(redirectLink)
                    }
                }
            }
        }
        authorizeUser()
    }, [
        redirectState,
        ornaBackendClient,
        qStrings,
        redirectLink,
        setNavigateTo,
        setOrganizationId,
    ])

    // no orgs found for email because
    // user account has been disabled across all organizations
    if (errorFetchingOrganizations) {
        return (
            <Fragment>
                <div className="flex h-screen w-screen flex-col items-center justify-center text-center text-3xl">
                    <p className="m-0">
                        Whoops, we didn't expect this. Please try refreshing
                        this page.
                    </p>
                    <button
                        className="mt-4 rounded-sm bg-primary-red-500 px-3 py-2 text-xs text-white focus:outline-none 2xl:px-4 2xl:py-2 2xl:text-sm"
                        onClick={() => {
                            localStorage.removeItem('access_token')
                            localStorage.removeItem('userOrganizationId')
                            localStorage.removeItem('userOrganizations')
                            logout()
                        }}
                    >
                        LOGOUT
                    </button>
                </div>
            </Fragment>
        )
    }

    if (isLoading) {
        return <Spinner />
    }

    if (organizationsLoading) {
        return <Spinner />
    }

    if (!userEmail) {
        return <Spinner />
    }

    if (
        (!organizations || organizations.length === 0) &&
        redirectState === null
    ) {
        return (
            <div className="flex h-screen w-screen flex-col items-center justify-center text-center text-3xl">
                <p className="m-0">
                    Whoops, looks like you haven't been added to an organization
                    yet.
                    <br />
                    Please contact your Organization Administrator.
                </p>
                <button
                    className="mt-12 rounded-sm bg-primary-red-500 px-3 py-2 text-xs text-white focus:outline-none 2xl:px-4 2xl:py-2 2xl:text-sm"
                    onClick={() => {
                        localStorage.removeItem('access_token')
                        localStorage.removeItem('userOrganizationId')
                        localStorage.removeItem('userOrganizations')
                        logout()
                    }}
                >
                    LOGOUT
                </button>
            </div>
        )
    }

    if (organizations?.length > 0) {
        if (organizations.length === 0 && redirectState) {
            return (
                <>
                    <div id="login-container" className="flex h-full w-full">
                        <div className="banner-container flex h-full w-8/12 flex-col justify-between py-10 pl-14">
                            <CommonBanner />
                        </div>
                        <div className="h-full w-4/12 bg-neutral-gray-500">
                            <div
                                className="flex h-full w-full flex-col items-center justify-center px-4 transition-opacity duration-300 ease-in"
                                style={{
                                    opacity: organizations !== null ? 1 : 0,
                                }}
                            >
                                <Fragment>
                                    <div
                                        className="h-auto w-full cursor-pointer rounded-md bg-neutral-gray-400 px-4 py-4 text-center hover:bg-neutral-gray-600"
                                        onClick={() => {
                                            history.replace(routes.signup)
                                        }}
                                    >
                                        Create a new org using{' '}
                                        <span className="text-primary-red-500">
                                            {redirectState?.redirectEmail}
                                        </span>
                                    </div>
                                    <button
                                        className="mt-4 rounded-sm bg-primary-red-500 px-3 py-2 text-xs text-white focus:outline-none 2xl:px-4 2xl:py-2 2xl:text-sm"
                                        onClick={() => {
                                            localStorage.removeItem(
                                                'access_token'
                                            )
                                            localStorage.removeItem(
                                                'userOrganizationId'
                                            )
                                            localStorage.removeItem(
                                                'userOrganizations'
                                            )
                                            logout()
                                        }}
                                    >
                                        LOGOUT
                                    </button>
                                </Fragment>
                            </div>
                        </div>
                    </div>
                </>
            )
        }

        return (
            <>
                {isSmallScreen ? (
                    <div
                        id="login-container"
                        className="flex h-full w-full justify-center"
                    >
                        <div className="h-full bg-neutral-gray-500">
                            <div
                                className="flex h-full w-full flex-col items-center px-4 transition-opacity duration-300 ease-in"
                                style={{
                                    opacity: organizations !== null ? 1 : 0,
                                }}
                            >
                                <div className="mt-8">
                                    <img
                                        className="mx-auto h-24 w-24"
                                        src={OrnaLogo}
                                        alt="orna logo"
                                    />
                                </div>
                                <div className="mt-8 w-full pt-1 text-center">
                                    <span className="font-Roboto text-3xl font-light text-white">
                                        Select Organization
                                    </span>
                                </div>
                                <div className="mt-6 h-auto w-full rounded-md bg-neutral-gray-400 px-12 py-10 pt-6">
                                    <div className="w-full">
                                        {organizations?.length > 0
                                            ? organizations.map(
                                                  (item, index) => (
                                                      <Fragment key={index}>
                                                          <div
                                                              className={`mt-4 mr-8 flex w-full cursor-pointer items-center rounded-md bg-neutral-gray-500 p-4 shadow-lg hover:bg-primary-red-500`}
                                                              onClick={(e) => {
                                                                  e.stopPropagation()
                                                                  //   setSelectOrganization(item)
                                                                  handleSelectOrganization(
                                                                      item.organizationId
                                                                  )
                                                              }}
                                                          >
                                                              <div>
                                                                  {item
                                                                      .organization
                                                                      .attachments
                                                                      .iconLogo ? (
                                                                      <img
                                                                          className="rounded-50-percent h-10 w-10"
                                                                          src={
                                                                              item
                                                                                  .organization
                                                                                  .attachments
                                                                                  .iconLogo
                                                                          }
                                                                          alt={`${item.organization.organizationId}`}
                                                                      />
                                                                  ) : (
                                                                      <div className="rounded-50-percent h-10 w-10 bg-neutral-gray-75"></div>
                                                                  )}
                                                              </div>
                                                              <div className="pl-4 text-white">
                                                                  {
                                                                      item
                                                                          .organization
                                                                          .legalName
                                                                  }
                                                              </div>
                                                          </div>
                                                      </Fragment>
                                                  )
                                              )
                                            : null}
                                    </div>
                                </div>
                                <div className="mt-8 flex w-full items-center justify-center px-6">
                                    <button
                                        className="w-64 rounded bg-primary-red-500 px-4 py-3 text-center font-Arial text-12-14 font-medium text-white"
                                        onClick={() => {
                                            localStorage.removeItem(
                                                'access_token'
                                            )
                                            localStorage.removeItem(
                                                'userOrganizationId'
                                            )
                                            localStorage.removeItem(
                                                'userOrganizations'
                                            )
                                            logout()
                                        }}
                                    >
                                        LOGOUT
                                    </button>
                                </div>
                                {redirectState &&
                                userEmail &&
                                redirectState.redirectEmail === userEmail ? (
                                    <Fragment>
                                        <h3 className="my-8">Or</h3>
                                        <div
                                            className="h-auto w-full cursor-pointer rounded-md bg-neutral-gray-400 px-4 py-4 text-center hover:bg-neutral-gray-600"
                                            onClick={() => {
                                                history.replace(routes.signup)
                                            }}
                                        >
                                            Create a new org using{' '}
                                            <span className="text-primary-red-500">
                                                {redirectState?.redirectEmail}
                                            </span>
                                        </div>
                                    </Fragment>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div id="login-container" className="flex h-full w-full">
                        <div className="banner-container flex h-full w-8/12 flex-col justify-between py-10 pl-14">
                            <CommonBanner />
                        </div>
                        <div className="h-full w-4/12 bg-neutral-gray-500">
                            <div
                                className="flex h-full w-full flex-col items-center justify-center px-4 transition-opacity duration-300 ease-in"
                                style={{
                                    opacity: organizations ? 1 : 0,
                                }}
                            >
                                <div className="h-auto w-full rounded-md bg-neutral-gray-400 px-4 py-4">
                                    <div className="text-lg">
                                        Select organization
                                    </div>
                                    <div className="">
                                        {organizations?.length > 0
                                            ? organizations.map(
                                                  (item, index) => (
                                                      <Fragment key={index}>
                                                          <button
                                                              className={`mt-4 flex w-full cursor-pointer items-center rounded-md bg-neutral-gray-500 px-2 py-2 shadow-lg ${
                                                                  item
                                                                      .organization
                                                                      .isRevoked
                                                                      ? 'cursor-not-allowed select-none opacity-40'
                                                                      : 'hover:bg-primary-red-500'
                                                              }`}
                                                              onClick={(e) => {
                                                                  if (
                                                                      item
                                                                          .organization
                                                                          .isRevoked
                                                                  ) {
                                                                      openNotification(
                                                                          alerts.ERROR,
                                                                          'Access revoked',
                                                                          10
                                                                      )
                                                                      return
                                                                  }

                                                                  e.stopPropagation()
                                                                  //   setSelectOrganization(item)
                                                                  handleSelectOrganization(
                                                                      item.organizationId
                                                                  )
                                                              }}
                                                          >
                                                              <div>
                                                                  {item
                                                                      .organization
                                                                      .attachments
                                                                      .iconLogo ? (
                                                                      <img
                                                                          className="rounded-50-percent h-10 w-10"
                                                                          src={
                                                                              item
                                                                                  .organization
                                                                                  .attachments
                                                                                  .iconLogo
                                                                          }
                                                                          alt={`${item.organization.organizationId}`}
                                                                      />
                                                                  ) : (
                                                                      <div className="rounded-50-percent h-10 w-10 bg-neutral-gray-75"></div>
                                                                  )}
                                                              </div>
                                                              <div className="pl-4 text-white">
                                                                  {
                                                                      item
                                                                          .organization
                                                                          .legalName
                                                                  }
                                                              </div>
                                                          </button>
                                                      </Fragment>
                                                  )
                                              )
                                            : null}
                                    </div>
                                </div>
                                <button
                                    className="mt-4 rounded-sm bg-primary-red-500 px-3 py-2 text-xs text-white focus:outline-none 2xl:px-4 2xl:py-2 2xl:text-sm"
                                    onClick={() => {
                                        localStorage.removeItem('access_token')
                                        localStorage.removeItem(
                                            'userOrganizationId'
                                        )
                                        localStorage.removeItem(
                                            'userOrganizations'
                                        )
                                        logout()
                                    }}
                                >
                                    LOGOUT
                                </button>
                                {redirectState &&
                                userEmail &&
                                redirectState.redirectEmail === userEmail ? (
                                    <Fragment>
                                        <h3 className="my-8">Or</h3>
                                        <div
                                            className="h-auto w-full cursor-pointer rounded-md bg-neutral-gray-400 px-4 py-4 text-center hover:bg-neutral-gray-600"
                                            onClick={() => {
                                                history.replace(routes.signup)
                                            }}
                                        >
                                            Create a new org using{' '}
                                            <span className="text-primary-red-500">
                                                {redirectState?.redirectEmail}
                                            </span>
                                        </div>
                                    </Fragment>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}
            </>
        )
    }

    return null
}

export default withSentryWrapper(SelectOrganizationView)
